import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { Sparkles, ChevronLeft, ChevronRight, ExternalLink, Handshake } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Featured Sponsors carousel — horizontally-scrolling ribbon of paid
 * sponsors from the last 7 days, shown at the top of the Community feed.
 * Purely public data (company_name, website, shoutout_note); nothing sensitive.
 */
const SponsorsCarousel = ({ days = 7 }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/sponsors/featured?days=${days}&limit=20`);
        setItems(res.data || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [days]);

  const scrollBy = (dir) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (loading || items.length === 0) return null;

  return (
    <section className="mb-6" data-testid="sponsors-carousel">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#EC008C]/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#EC008C]" />
          </div>
          <div>
            <h3 className="font-['Unbounded'] text-sm font-bold text-white">Featured Sponsors</h3>
            <p className="text-[10px] text-[#8A92A0]">Brands supporting the Movement</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/sponsors" className="text-xs text-[#EC008C] hover:underline font-semibold" data-testid="carousel-become-sponsor">
            Become a Sponsor →
          </Link>
          <div className="hidden sm:flex items-center gap-1">
            <button
              onClick={() => scrollBy(-1)}
              className="w-8 h-8 rounded-full bg-[#0A0E18] border border-[#1A2030] flex items-center justify-center text-[#8A92A0] hover:text-white hover:border-[#EC008C] transition-colors"
              aria-label="Scroll left"
              data-testid="carousel-scroll-left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollBy(1)}
              className="w-8 h-8 rounded-full bg-[#0A0E18] border border-[#1A2030] flex items-center justify-center text-[#8A92A0] hover:text-white hover:border-[#EC008C] transition-colors"
              aria-label="Scroll right"
              data-testid="carousel-scroll-right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "thin", scrollbarColor: "#293B35 transparent" }}
        data-testid="carousel-scroller"
      >
        {items.map((s) => {
          const initials = (s.company_name || "?").split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";
          const site = s.website || "";
          return (
            <article
              key={s.contribution_id}
              className="flex-shrink-0 snap-start w-72 rounded-2xl bg-[#0A0E18] border border-[#1A2030] hover:border-[#EC008C]/40 p-4 transition-colors"
              data-testid={`sponsor-card-${s.contribution_id}`}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#EC008C]/25 to-[#EC008C]/5 flex items-center justify-center flex-shrink-0 border border-[#EC008C]/30">
                  <span className="font-['Unbounded'] text-sm font-black text-[#EC008C]">{initials}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{s.company_name}</p>
                  <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] uppercase tracking-wider text-[#8A92A0]">
                    <Handshake className="w-3 h-3 text-[#EC008C]" /> Sponsor
                  </span>
                </div>
              </div>
              <p className="text-xs text-[#C0C6D0] leading-relaxed line-clamp-3 min-h-[3rem] mb-3">
                {s.shoutout_note || "Supporting the Movement — thank you!"}
              </p>
              {site ? (
                <a
                  href={site.startsWith("http") ? site : `https://${site}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#EC008C] hover:underline"
                  data-testid={`sponsor-visit-${s.contribution_id}`}
                >
                  Visit site <ExternalLink className="w-3 h-3" />
                </a>
              ) : s.contact_email ? (
                <span
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#8A92A0]"
                  data-testid={`sponsor-contact-${s.contribution_id}`}
                  title="StardomFans.com does not surface external emails — reach out via Community post."
                >
                  Contact via Community post
                </span>
              ) : (
                <span className="text-xs text-[#4A5260]">Contact via Community post</span>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default SponsorsCarousel;
